/**
 * Plugin del module kind `assignment` (F3-B) — tarea/examen/proyecto/participación.
 *
 * Se registra en el CourseModuleRegistry SIN tocar el core (misma receta que
 * lesson/resource). El CONTENIDO (título, instrucciones, rúbrica, tipo de
 * entrega) vive en la fila `assignment` (ref_id); las ENTREGAS y NOTAS son por
 * cohorte (`assignment_submission`/`assignment_grade`) y alimentan el gradebook,
 * NO la completitud de lección del curso — por eso `completion/gradeContribution`
 * devuelven `null` (el peso de la tarea se decide en el gradebook de la cohorte).
 */

import type { FeatureManifest } from "../country/packs";
import {
  anyObjectValidator,
  type CourseModulePlugin,
  isPlainObject,
  type ModuleValidationResult,
} from "./types";

export type AssignmentKind = "homework" | "exam" | "project" | "participation";
export const ASSIGNMENT_KINDS: readonly AssignmentKind[] = [
  "homework",
  "exam",
  "project",
  "participation",
];

export type SubmissionType = "file" | "text" | "none";
export const SUBMISSION_TYPES: readonly SubmissionType[] = ["file", "text", "none"];

// ─── Rúbrica ───────────────────────────────────────────────────────────────────

export interface RubricLevel {
  label: string;
  points: number;
}
export interface RubricCriterion {
  name: string;
  description?: string;
  levels: RubricLevel[];
}
export interface Rubric {
  criteria: RubricCriterion[];
}

export function isAssignmentKind(v: unknown): v is AssignmentKind {
  return typeof v === "string" && (ASSIGNMENT_KINDS as readonly string[]).includes(v);
}
export function isSubmissionType(v: unknown): v is SubmissionType {
  return typeof v === "string" && (SUBMISSION_TYPES as readonly string[]).includes(v);
}

/**
 * Valida (y normaliza) una rúbrica libre. Tolerante: descarta niveles/criterios
 * malformados en vez de fallar (v1). Devuelve `null` si no hay rúbrica usable.
 */
export function normalizeRubric(value: unknown): Rubric | null {
  if (!isPlainObject(value)) {
    return null;
  }
  const rawCriteria = value.criteria;
  if (!Array.isArray(rawCriteria)) {
    return null;
  }
  const criteria: RubricCriterion[] = [];
  for (const c of rawCriteria) {
    if (!isPlainObject(c) || typeof c.name !== "string" || c.name.trim() === "") {
      continue;
    }
    const levels: RubricLevel[] = [];
    if (Array.isArray(c.levels)) {
      for (const l of c.levels) {
        if (!isPlainObject(l)) {
          continue;
        }
        const label = typeof l.label === "string" ? l.label : "";
        const points = typeof l.points === "number" ? l.points : Number(l.points);
        if (label.trim() === "" || Number.isNaN(points)) {
          continue;
        }
        levels.push({ label, points });
      }
    }
    criteria.push({
      name: c.name,
      description: typeof c.description === "string" ? c.description : undefined,
      levels,
    });
  }
  return criteria.length > 0 ? { criteria } : null;
}

/** Suma del máximo de puntos de la rúbrica (mayor `points` por criterio). */
export function rubricMaxPoints(rubric: Rubric | null): number {
  if (!rubric) {
    return 0;
  }
  return rubric.criteria.reduce((total, c) => {
    const best = c.levels.reduce((m, l) => Math.max(m, l.points), 0);
    return total + best;
  }, 0);
}

/** Suma de puntos de una selección de rúbrica ({criterio: puntos}). */
export function rubricScoreTotal(scores: Record<string, number> | null | undefined): number {
  if (!scores) {
    return 0;
  }
  return Object.values(scores).reduce((sum, p) => sum + (typeof p === "number" ? p : 0), 0);
}

// ─── Manifest + plugin ─────────────────────────────────────────────────────────

export const ASSIGNMENT_MANIFEST: FeatureManifest = {
  key: "academy.module.assignment",
  name: "Tarea evaluable",
  description:
    "Tarea, examen, proyecto o participación con rúbrica opcional, entrega (archivo/texto) y calificación por cohorte que alimenta el libro de notas.",
  scope: "course",
  status: "stable",
  gateable: true,
  since: "2026-06",
  docsSlug: "profesor",
  implications: {
    navHidden: ["Paradas «Tarea» en Aprender", "«Tarea» en el menú de módulos"],
    flowsBlocked: ["Entregar tareas (texto/archivo)"],
    dataPolicy: "preserved",
  },
};

export function assignmentModuleManifest(): FeatureManifest {
  return ASSIGNMENT_MANIFEST;
}

export function createAssignmentModulePlugin<TComponent = unknown>(
  components: {
    authoringComponent?: () => Promise<TComponent>;
    playerComponent?: () => Promise<TComponent>;
  } = {},
): CourseModulePlugin<TComponent> {
  return {
    kind: "assignment",
    manifest: ASSIGNMENT_MANIFEST,
    // La config del course_module para assignment es libre (detalle en `assignment`).
    configSchema: anyObjectValidator(),
    authoringComponent: components.authoringComponent,
    playerComponent: components.playerComponent,
    // La tarea NO aporta a la completitud/nota de LECCIÓN del curso: su peso se
    // decide en el gradebook de la COHORTE (grade_item → assignment).
    completionContribution: (): number | null => null,
    gradeContribution: (): number | null => null,
  };
}

/** Valida una config de assignment (siempre objeto; v1 sin campos requeridos). */
export function validateAssignmentConfig(
  value: unknown,
): ModuleValidationResult<Record<string, unknown>> {
  return anyObjectValidator().parse(value);
}
